import { cn } from "@/lib/cn"
import * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"
import { TranslationValues, useTranslations } from "next-intl"
import {
  ComponentPropsWithRef,
  createContext,
  ReactNode,
  use,
  useId,
} from "react"
import { FieldError, FormProvider, useFormContext } from "react-hook-form"
import { Label } from "./ui/label"

export enum FieldUIStates {
  idle = "idle",
  warn = "warn",
  error = "error",
}

type FormFieldContextValue = {
  id: string
  name: string
  formItemId: string
  formDescriptionId: string
  formMessageId: string
} & ReturnType<FieldStateReducer> &
  FieldState

type FieldState = {
  invalid: boolean
  isDirty: boolean
  isTouched: boolean
  error?: FieldError
}

export type TransformErrorMessageFn = (message: string) => {
  message: string
  messageProps?: TranslationValues
}

type FIeldStateReducerFactoryProps = {
  defaultUiState?: FieldUIStates
  warnMessage?: string
  transformErrorMessage?: TransformErrorMessageFn
}

export type FieldStateReducer = (fieldState: FieldState) => {
  fieldUiState: FieldUIStates
  errorMessage: null | ReturnType<TransformErrorMessageFn>
}

type FormFieldProps = {
  name: string
  fieldStateReducer?: FieldStateReducer
  children: ReactNode
} & ComponentPropsWithRef<"div">

/* -------------------------------------------------------------------------------------------------
 * context
 * -----------------------------------------------------------------------------------------------*/

const FormFieldContext = createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
)

/* -------------------------------------------------------------------------------------------------
 * utils
 * -----------------------------------------------------------------------------------------------*/

//this is a HOF that we can use to create a handler to manipulate the field ui state
export function createFieldStateReducer({
  defaultUiState = FieldUIStates.idle,
  warnMessage,
  transformErrorMessage,
}: FIeldStateReducerFactoryProps): FieldStateReducer {
  return function (fieldState: FieldState) {
    const { error, isTouched } = fieldState
    const message = error?.message

    //if error is of type custom it means that we are forcing it so we want to show the error message regardless if it's touched or not
    if (
      !(typeof message === "string" && (isTouched || error?.type === "custom"))
    )
      return {
        fieldUiState: defaultUiState,
        errorMessage: null,
      }

    const errorMessage =
      typeof transformErrorMessage === "function"
        ? transformErrorMessage(message)
        : { message }

    if (message === warnMessage)
      return {
        fieldUiState: FieldUIStates.warn,
        errorMessage,
      }

    return {
      fieldUiState: FieldUIStates.error,
      errorMessage,
    }
  }
}

export const defaultStateReducer = createFieldStateReducer({})

export const Form = FormProvider

export function FormField({
  className,
  name,
  fieldStateReducer = defaultStateReducer,
  ...props
}: FormFieldProps) {
  const id = useId()

  const { getFieldState, formState } = useFormContext()

  const fieldState = getFieldState(name, formState)

  const computedFieldState = fieldStateReducer(fieldState)
  return (
    <FormFieldContext.Provider
      value={{
        id,
        name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...computedFieldState,
        ...fieldState,
      }}
    >
      <div
        className={cn("group", className)}
        data-ui-state={computedFieldState.fieldUiState}
        {...props}
      />
    </FormFieldContext.Provider>
  )
}

/* -----------------------------------------------------------------------------------------------*/

export function FormGroup({
  className,
  ...props
}: ComponentPropsWithRef<"div">) {
  return <div className={cn("relative", className)} {...props} />
}

/* -----------------------------------------------------------------------------------------------*/

export function FormLabel({
  className,
  ...props
}: ComponentPropsWithRef<typeof LabelPrimitive.Root>) {
  const { formItemId } = use(FormFieldContext)

  return (
    <Label
      className={cn(
        `bg-field-background peer-focus:bg-field-background text-field-label-floating group-data-[ui-state=error]:text-field-error-label group-data-[ui-state=warn]:text-field-warning peer-focus:group-data-[ui-state=idle]:text-field-label-floating peer-focus:group-data-[ui-state=error]:text-field-error pointer-events-none absolute top-2 z-10 ml-[.625rem] origin-[0] translate-y-[-1.1rem] scale-[.8] rounded-lg px-[.625rem] duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:bg-transparent peer-focus:top-2 peer-focus:translate-y-[-1.1rem] peer-focus:scale-[.8]`,
        className,
      )}
      htmlFor={formItemId}
      {...props}
    />
  )
}

/* -----------------------------------------------------------------------------------------------*/

export function FormControl({
  ref,
  ...props
}: ComponentPropsWithRef<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } =
    use(FormFieldContext)

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
}

export const FormMessage = ({
  className,
  children,
  ...props
}: ComponentPropsWithRef<"p">) => {
  const { errorMessage, formMessageId } = use(FormFieldContext)
  const body = errorMessage ? String(errorMessage?.message) : children
  const messageProps = errorMessage?.messageProps || {}
  const t = useTranslations()

  if (!body) {
    return null
  }

  return (
    <p
      id={formMessageId}
      className={cn(
        "text-paragraph-s group-data-[ui-state=error]:text-field-error group-data-[ui-state=warn]:text-field-warning mt-1 font-normal",
        className,
      )}
      {...props}
    >
      {typeof body === "string" ? t(body, messageProps) : body}
    </p>
  )
}
